import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, map, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthSessionService } from '../../auth/services/auth-session.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import {
  CreatePartnerIntegrationRequestDto,
  DossierScopeCatalogResponse,
  normalizeIntegrationRequestsResponse,
  PartnerIntegrationRequest,
  PartnerIntegrationRequestsResponse,
  PartnerWebhookDeliveriesResponse,
  PartnerWebhookDelivery,
  PartnerWebhookSecretResponse,
  UpdatePartnerIntegrationRequestDto,
} from '../models/partner-integration.model';

@Injectable({ providedIn: 'root' })
export class PatrolIntegrationService {
  #http = inject(HttpClient);
  #session = inject(AuthSessionService);
  #subdomain = inject(SubdomainService);
  #qc = inject(QueryClient);

  readonly enabled = computed(
    () =>
      this.#session.isAuthenticated() && this.#subdomain.isPatrolSubdomain(),
  );

  readonly scopeCatalogQuery = injectQuery(() => ({
    queryKey: ['patrol', 'integration', 'scopes'],
    queryFn: () => this.fetchScopeCatalog(),
    enabled: this.enabled(),
    staleTime: 60_000,
  }));

  readonly requestsQuery = injectQuery(() => ({
    queryKey: ['patrol', 'integration-requests'],
    queryFn: () => this.fetchRequests(),
    enabled: this.enabled(),
    staleTime: 15_000,
  }));

  readonly requests = computed(
    () => this.requestsQuery.data() ?? [],
  );

  readonly createMutation = injectMutation(() => ({
    mutationFn: (dto: CreatePartnerIntegrationRequestDto) =>
      this.createRequest(dto),
    onSuccess: () => this.invalidateRequests(),
  }));

  readonly updateMutation = injectMutation(() => ({
    mutationFn: ({
      requestId,
      dto,
    }: {
      requestId: string;
      dto: UpdatePartnerIntegrationRequestDto;
    }) => this.updateRequest(requestId, dto),
    onSuccess: () => this.invalidateRequests(),
  }));

  readonly submitMutation = injectMutation(() => ({
    mutationFn: (requestId: string) => this.submitRequest(requestId),
    onSuccess: () => this.invalidateRequests(),
  }));

  readonly configureWebhookMutation = injectMutation(() => ({
    mutationFn: ({
      requestId,
      webhookUrl,
    }: {
      requestId: string;
      webhookUrl: string;
    }) => this.configureWebhook(requestId, webhookUrl),
    onSuccess: () => this.invalidateRequests(),
  }));

  readonly resendWebhookMutation = injectMutation(() => ({
    mutationFn: ({
      requestId,
      deliveryId,
    }: {
      requestId: string;
      deliveryId: string;
    }) => this.resendWebhookDelivery(requestId, deliveryId),
    onSuccess: (_data, variables) =>
      this.invalidateWebhookDeliveries(variables.requestId),
  }));

  invalidateRequests(): void {
    void this.#qc.invalidateQueries({
      queryKey: ['patrol', 'integration-requests'],
    });
  }

  invalidateWebhookDeliveries(requestId: string): void {
    void this.#qc.invalidateQueries({
      queryKey: ['patrol', 'webhook-deliveries', requestId],
    });
  }

  fetchWebhookDeliveries(requestId: string): Promise<PartnerWebhookDelivery[]> {
    return lastValueFrom(
      this.#http
        .get<PartnerWebhookDeliveriesResponse>(
          `${environment.baseUrl}/user/patrol/integration-requests/${encodeURIComponent(requestId)}/webhook-deliveries`,
        )
        .pipe(
          map((response) =>
            (response?.items ?? []).map((item) => ({
              ...item,
              createdAt: normalizeDeliveryUnix(item.createdAt),
            })),
          ),
          take(1),
        ),
    );
  }

  private fetchScopeCatalog(): Promise<DossierScopeCatalogResponse> {
    return lastValueFrom(
      this.#http
        .get<DossierScopeCatalogResponse>(
          `${environment.baseUrl}/user/patrol/integration/scopes`,
        )
        .pipe(take(1)),
    );
  }

  private fetchRequests(): Promise<PartnerIntegrationRequest[]> {
    return lastValueFrom(
      this.#http
        .get<PartnerIntegrationRequestsResponse>(
          `${environment.baseUrl}/user/patrol/integration-requests`,
        )
        .pipe(
          map((response) => normalizeIntegrationRequestsResponse(response)),
          take(1),
        ),
    );
  }

  private createRequest(
    dto: CreatePartnerIntegrationRequestDto,
  ): Promise<PartnerIntegrationRequest> {
    return lastValueFrom(
      this.#http
        .post<PartnerIntegrationRequest>(
          `${environment.baseUrl}/user/patrol/integration-requests`,
          dto,
        )
        .pipe(take(1)),
    );
  }

  private updateRequest(
    requestId: string,
    dto: UpdatePartnerIntegrationRequestDto,
  ): Promise<PartnerIntegrationRequest> {
    return lastValueFrom(
      this.#http
        .patch<PartnerIntegrationRequest>(
          `${environment.baseUrl}/user/patrol/integration-requests/${encodeURIComponent(requestId)}`,
          dto,
        )
        .pipe(take(1)),
    );
  }

  private submitRequest(
    requestId: string,
  ): Promise<PartnerIntegrationRequest> {
    return lastValueFrom(
      this.#http
        .post<PartnerIntegrationRequest>(
          `${environment.baseUrl}/user/patrol/integration-requests/${encodeURIComponent(requestId)}/submit`,
          {},
        )
        .pipe(take(1)),
    );
  }

  private configureWebhook(
    requestId: string,
    webhookUrl: string,
  ): Promise<PartnerWebhookSecretResponse> {
    return lastValueFrom(
      this.#http
        .post<PartnerWebhookSecretResponse>(
          `${environment.baseUrl}/user/patrol/integration-requests/${encodeURIComponent(requestId)}/webhook`,
          { webhookUrl },
        )
        .pipe(take(1)),
    );
  }

  private resendWebhookDelivery(
    requestId: string,
    deliveryId: string,
  ): Promise<PartnerWebhookDelivery> {
    return lastValueFrom(
      this.#http
        .post<PartnerWebhookDelivery>(
          `${environment.baseUrl}/user/patrol/integration-requests/${encodeURIComponent(requestId)}/webhook-deliveries/${encodeURIComponent(deliveryId)}/resend`,
          {},
        )
        .pipe(take(1)),
    );
  }
}

function normalizeDeliveryUnix(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) && asNumber > 0 ? asNumber : undefined;
  }
  return undefined;
}
